import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ExternalLink, Eye, EyeOff, FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type RowAction,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Page, PageStatus } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { openStorefrontUrl } from '@/features/stores';
import { useStore } from '@/shared/store/store-context';

import { DeletePageDialog } from '../components/delete-page-dialog';
import { usePageColumns } from '../components/page-columns';
import { pageFilters } from '../components/page-filters';
import { useUpdatePage } from '../hooks/use-page-mutations';
import { usePages } from '../hooks/use-pages';

const ROUTE_ID = '/_authed/_active/s/$storeId/content/pages/' as const;
const ROUTE_PATH = '/s/$storeId/content/pages/' as const;

function NewPageButton({ storeId, tour }: { storeId: string; tour?: string }) {
  const { t } = useTranslation('pages');
  return (
    <Can permission="content.write">
      <Button asChild data-tour={tour}>
        <Link to="/s/$storeId/content/pages/new" params={{ storeId }}>
          <Plus aria-hidden />
          {t('new')}
        </Link>
      </Button>
    </Can>
  );
}

function Pages() {
  const { t } = useTranslation('pages');
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const message = useErrorMessage();
  const { can } = usePermissions();
  const canWrite = can('content.write');
  const update = useUpdatePage(store.id, 'pages:toasts.statusChanged');
  const [deleting, setDeleting] = useState<Page | null>(null);

  const filters = useMemo(() => pageFilters(t), [t]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = usePages(store.id, list.params);

  const mutateUpdate = update.mutate;
  const setStatus = useCallback(
    (row: Page, status: PageStatus) =>
      mutateUpdate(
        { id: row.id, body: { status } },
        { onError: (error) => toast.error(message(error)) },
      ),
    [message, mutateUpdate],
  );

  const open = useCallback(
    (row: Page) =>
      void navigate({
        to: '/s/$storeId/content/pages/$pageId',
        params: { storeId: store.id, pageId: row.id },
      }),
    [navigate, store.id],
  );

  const rowActions = useMemo<Array<RowAction<Page>>>(
    () => [
      { id: 'open', label: t('actions.open'), icon: <Pencil aria-hidden />, onAction: open },
      {
        id: 'view',
        label: t('actions.view'),
        icon: <ExternalLink aria-hidden />,
        hidden: (row) => !row.storefrontUrl || row.status !== 'published',
        onAction: (row) => openStorefrontUrl(row.storefrontUrl),
      },
      {
        id: 'unpublish',
        label: t('actions.unpublish'),
        icon: <EyeOff aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status !== 'published',
        onAction: (row) => setStatus(row, 'draft'),
      },
      {
        id: 'publish',
        label: t('actions.publish'),
        icon: <Eye aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status === 'published',
        onAction: (row) => setStatus(row, 'published'),
      },
      {
        id: 'delete',
        label: t('actions.delete'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        hidden: () => !canWrite,
        onAction: (row) => setDeleting(row),
      },
    ],
    [canWrite, open, setStatus, t],
  );

  const columns = usePageColumns(store.id);
  const rows = query.data?.items ?? [];
  const table = useDataTable({
    mode: 'server',
    data: rows,
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'pages',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && rows.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={<NewPageButton storeId={store.id} tour="content.pages.new" />}
      />
      {isEmptyList ? (
        <EmptyState
          icon={FileText}
          title={t('empty.title')}
          description={t('empty.description')}
          action={<NewPageButton storeId={store.id} />}
          tour="content.pages.empty"
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          tour={{ table: 'content.pages.table' }}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching || update.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
          rowActions={rowActions}
          onRowClick={open}
          getRowLabel={(row) => row.title}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('empty.filtered') }}
        />
      )}
      <DeletePageDialog
        storeId={store.id}
        page={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function PagesPage() {
  return (
    <FeatureErrorBoundary name="pages.list">
      <Pages />
    </FeatureErrorBoundary>
  );
}
